import React from 'react';
import './SetupStatusWidget.css';
import { useT } from '../i18n';

const messages = {
  title: { tr: 'Kurulum Durumu', en: 'Setup Status' },
  progress: { tr: 'İlerleme', en: 'Progress' },
  appointment: { tr: 'Kurulum Randevunuz', en: 'Your Setup Appointment' },
  joinMeeting: { tr: 'Toplantıya Katıl', en: 'Join Meeting' },
  nextStep: { tr: 'Sıradaki Adım', en: 'Next Step' },
  contactSupport: { tr: 'Destek ile İletişime Geç', en: 'Contact Support' },
  statuses: {
    tr: {},
    en: {
      pending: 'Pending', approved: 'Approved', admin_created: 'Created', appointment_scheduled: 'Appointment scheduled',
      setup_in_progress: 'Setup in progress', setup_completed: 'Setup completed', rejected: 'Rejected', cancelled: 'Cancelled',
    },
  },
};

const ICON_PATHS = {
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" /></>,
  check: <><circle cx="12" cy="12" r="9" /><path d="M8.5 12.5l2.5 2.5 4.5-5" /></>,
  calendar: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></>,
  gear: <><circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3M5.5 5.5l2 2M16.5 16.5l2 2M18.5 5.5l-2 2M7.5 16.5l-2 2" /></>,
  x: <><circle cx="12" cy="12" r="9" /><path d="M9.5 9.5l5 5M14.5 9.5l-5 5" /></>,
  ban: <><circle cx="12" cy="12" r="9" /><path d="M6 6l12 12" /></>,
  list: <><path d="M8 6h13M8 12h13M8 18h13" /><path d="M3 6h.01M3 12h.01M3 18h.01" /></>,
  link: <><path d="M9 15l6-6" /><path d="M11 6l1-1a4 4 0 0 1 6 6l-1 1" /><path d="M13 18l-1 1a4 4 0 0 1-6-6l1-1" /></>,
  arrowRight: <path d="M5 12h14M13 6l6 6-6 6" />,
  chat: <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-4-1L3 20l1-5.5a8.38 8.38 0 0 1-1-4A8.5 8.5 0 0 1 12.5 2 8.38 8.38 0 0 1 21 11.5z" />,
};

function Icon({ name, className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      {ICON_PATHS[name]}
    </svg>
  );
}

const SetupStatusWidget = ({ status }) => {
  const { t, locale } = useT(messages);
  if (!status) return null;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusTone = (statusKey) => {
    const tones = {
      'pending': 'warning',
      'approved': 'neutral',
      'admin_created': 'neutral',
      'appointment_scheduled': 'success',
      'setup_in_progress': 'accent',
      'setup_completed': 'success',
      'rejected': 'danger',
      'cancelled': 'muted',
    };
    return tones[statusKey] || 'muted';
  };

  const getStatusIconName = (statusKey) => {
    const icons = {
      'pending': 'clock',
      'approved': 'check',
      'admin_created': 'check',
      'appointment_scheduled': 'calendar',
      'setup_in_progress': 'gear',
      'setup_completed': 'check',
      'rejected': 'x',
      'cancelled': 'ban',
    };
    return icons[statusKey] || 'list';
  };

  const tone = getStatusTone(status.status);

  return (
    <div className="setup-status-widget">
      <div className="widget-header">
        <div className="header-content">
          <h3>{t('title')}</h3>
          <div className={`status-badge status-badge-${tone}`}>
            <Icon name={getStatusIconName(status.status)} />
            {t('statuses')[status.status] || status.status_display}
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="progress-container">
        <div className="progress-label">
          <span>{t('progress')}</span>
          <span className="progress-percentage">{status.progress_percentage}%</span>
        </div>
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${status.progress_percentage}%` }}
          />
        </div>
      </div>

      {/* Steps Timeline */}
      <div className="steps-timeline">
        {status.steps && status.steps.map((step, index) => (
          <div
            key={index}
            className={`timeline-step ${step.completed ? 'completed' : 'pending'}`}
          >
            <div className="step-icon">
              <Icon name={step.completed ? 'check' : 'clock'} />
            </div>
            <div className="step-content">
              <div className="step-name">{step.name}</div>
            </div>
            {index < status.steps.length - 1 && (
              <div className={`step-connector ${step.completed ? 'completed' : ''}`} />
            )}
          </div>
        ))}
      </div>

      {/* Appointment Info */}
      {status.scheduled_date && (
        <div className="appointment-info">
          <h4><Icon name="calendar" /> {t('appointment')}</h4>
          <p className="appointment-date">{formatDate(status.scheduled_date)}</p>
          {status.meeting_link && (
            <a
              href={status.meeting_link}
              target="_blank"
              rel="noopener noreferrer"
              className="meeting-link"
            >
              <Icon name="link" /> {t('joinMeeting')}
            </a>
          )}
        </div>
      )}

      {/* Next Step */}
      {status.next_step && (
        <div className="next-step">
          <div className="next-step-header">
            <strong><Icon name="arrowRight" /> {t('nextStep')}</strong>
          </div>
          <p>{status.next_step}</p>
        </div>
      )}

      {/* Contact Support */}
      {!status.is_setup_complete && (
        <div className="widget-footer">
          <button className="contact-support-btn">
            <Icon name="chat" /> {t('contactSupport')}
          </button>
        </div>
      )}
    </div>
  );
};

export default SetupStatusWidget;
